import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Grid, Button, TextField, Typography, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import AppDialogTitle from '../../../../ui/appDialogTitle';
import { ButtonType } from './invoiceButtonComponents';
import { FRAME_HANDLING_PATIENT_OWN } from '../invoice';
import { FRAME_LAB_STATUS_ENCLOSED, FRAME_LAB_STATUS_TO_COME, getFrameDetails } from './glassesOrderMapper';

const toNumber = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

const defaultForm = {
    modelNumber: '',
    brand: '',
    supplier: '',
    quantity: 1,
    price: 0,
    eyeSize: '',
    bridgeSize: '',
    templeLength: '',
    aMeasurement: '',
    bMeasurement: '',
    dblMeasurement: '',
    frameColor: '',
    material: '',
    shape: '',
    rimType: '',
    frameLabStatus: FRAME_LAB_STATUS_ENCLOSED,
  };

function ManualGlassesDialog({ onClose, open, onSelect, location, existingManualFrameData = null }) {
  const [form, setForm] = useState(defaultForm);

  const isEditing = Boolean(existingManualFrameData?.item);
  const dialogTitle = isEditing ? 'Edit Manual Frame' : 'Add Frame';
  const primaryButtonLabel = isEditing ? 'Update Frame' : 'Add to Invoice';

  const total = useMemo(() => toNumber(form.quantity, 1) * toNumber(form.price, 0), [form.price, form.quantity]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!existingManualFrameData?.item) {
      setForm(defaultForm);
      return;
    }

    const editingItem = existingManualFrameData.item;
    const details = getFrameDetails(editingItem) || {};
    const patientOwn = details.patientOwnFrame || {};
    const properties = details.properties || {};

    setForm({
      modelNumber: patientOwn.modelNumber || details.modelNumber || editingItem.modelNumber || '',
      brand: patientOwn.brand || details.brand?.name || editingItem.brand?.name || '',
      supplier: patientOwn.supplier || details.supplier?.supplierName || editingItem.supplier?.supplierName || '',
      quantity: editingItem.quantity || details.quantity || 1,
      price: editingItem.price ?? details.listPrice ?? 0,
      eyeSize: patientOwn.eyeSize ?? properties.eyeSize ?? 0,
      bridgeSize: patientOwn.bridgeSize ?? properties.bridgeSize ?? 0,
      templeLength: patientOwn.templeLength ?? properties.templeLength ?? 0,
      aMeasurement: patientOwn.aMeasurement ?? properties.aMeasurement ?? 0,
      bMeasurement: patientOwn.bMeasurement ?? properties.bMeasurement ?? 0,
      dblMeasurement: patientOwn.dblMeasurement ?? properties.dblMeasurement ?? 0,
      frameColor: patientOwn.frameColor || properties.frameColor || '',
      material: patientOwn.material || properties.material || '',
      shape: patientOwn.shape || properties.shape || '',
      rimType: patientOwn.rimType || properties.rimType || '',
      frameLabStatus: details.frameLabStatus || editingItem.frameLabStatus || FRAME_LAB_STATUS_ENCLOSED,
    });
  }, [existingManualFrameData, open]);

  const updateField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSave = () => {
    const modelNumber = String(form.modelNumber || '').trim();
    const description = modelNumber || 'Manual Frame';
    const quantity = Math.max(1, toNumber(form.quantity, 1));
    const price = Math.max(0, toNumber(form.price, 0));
    const editingItem = existingManualFrameData?.item || null;
    const groupId = existingManualFrameData?.groupId || editingItem?.groupId || uuidv4();
    const effectiveLocation = location || editingItem?.location || null;

    const patientOwnFrame = {
      invoiceItemId: null,
      modelNumber: modelNumber || null,
      supplier: String(form.supplier || '').trim() || null,
      brand: String(form.brand || '').trim() || null,
      eyeSize: toNumber(form.eyeSize, 0),
      bridgeSize: toNumber(form.bridgeSize, 0),
      templeLength: toNumber(form.templeLength, 0),
      aMeasurement: toNumber(form.aMeasurement, 0),
      bMeasurement: toNumber(form.bMeasurement, 0),
      dblMeasurement: toNumber(form.dblMeasurement, 0),
      edMeasurement: 0,
      circumference: 0,
      edAngle: 0,
      frameColor: String(form.frameColor || '').trim() || null,
      frameColorCode: null,
      material: String(form.material || '').trim() || null,
      shape: String(form.shape || '').trim() || null,
      rimType: String(form.rimType || '').trim() || null,
    };

    const frame = {
      itemId: null,
      frameHandling: FRAME_HANDLING_PATIENT_OWN,
      frameLabStatus: form.frameLabStatus || FRAME_LAB_STATUS_ENCLOSED,
      modelNumber: modelNumber || null,
      description,
      quantity,
      listPrice: price,
      brand: patientOwnFrame.brand ? { name: patientOwnFrame.brand } : null,
      supplier: patientOwnFrame.supplier ? { supplierName: patientOwnFrame.supplier } : null,
      properties: {
        eyeSize: patientOwnFrame.eyeSize,
        bridgeSize: patientOwnFrame.bridgeSize,
        templeLength: patientOwnFrame.templeLength,
        aMeasurement: patientOwnFrame.aMeasurement,
        bMeasurement: patientOwnFrame.bMeasurement,
        dblMeasurement: patientOwnFrame.dblMeasurement,
        frameColor: patientOwnFrame.frameColor || '',
        material: patientOwnFrame.material || '',
        shape: patientOwnFrame.shape || '',
        rimType: patientOwnFrame.rimType || '',
      },
      location: effectiveLocation,
      frameDetails: {
        mode: FRAME_HANDLING_PATIENT_OWN,
        itemId: null,
        modelNumber: modelNumber || null,
        description,
        brand: patientOwnFrame.brand ? { name: patientOwnFrame.brand } : null,
        supplier: patientOwnFrame.supplier ? { supplierName: patientOwnFrame.supplier } : null,
        properties: {
          eyeSize: patientOwnFrame.eyeSize,
          bridgeSize: patientOwnFrame.bridgeSize,
          templeLength: patientOwnFrame.templeLength,
          aMeasurement: patientOwnFrame.aMeasurement,
          bMeasurement: patientOwnFrame.bMeasurement,
          dblMeasurement: patientOwnFrame.dblMeasurement,
          frameColor: patientOwnFrame.frameColor || '',
          material: patientOwnFrame.material || '',
          shape: patientOwnFrame.shape || '',
          rimType: patientOwnFrame.rimType || '',
        },
        listPrice: price,
        quantity,
        location: effectiveLocation,
        frameLabStatus: form.frameLabStatus || FRAME_LAB_STATUS_ENCLOSED,
        stockFrame: null,
        patientOwnFrame,
        orderNewFrame: null,
      },
    };

    onSelect({
      name: 'Manual Frame',
      type: ButtonType.NEW_GLASSES,
      frame,
      lens: { OD: null, OS: null },
      rx: null,
      total,
      groupId,
      source: 'manual_frame',
    });
  };

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="md">
      <AppDialogTitle onClose={onClose} title={dialogTitle} />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Capture frame details without selecting inventory stock.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Model Number" value={form.modelNumber} onChange={updateField('modelNumber')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Brand" value={form.brand} onChange={updateField('brand')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Supplier" value={form.supplier} onChange={updateField('supplier')} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="number" label="Quantity" value={form.quantity} onChange={updateField('quantity')} inputProps={{ min: 1 }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="number" label="Price" value={form.price} onChange={updateField('price')} inputProps={{ min: 0, step: '0.01' }} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="Eye Size" value={form.eyeSize} onChange={updateField('eyeSize')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="Bridge Size" value={form.bridgeSize} onChange={updateField('bridgeSize')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="Temple Length" value={form.templeLength} onChange={updateField('templeLength')} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="A Measurement" value={form.aMeasurement} onChange={updateField('aMeasurement')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="B Measurement" value={form.bMeasurement} onChange={updateField('bMeasurement')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="DBL Measurement" value={form.dblMeasurement} onChange={updateField('dblMeasurement')} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Frame Color" value={form.frameColor} onChange={updateField('frameColor')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Material" value={form.material} onChange={updateField('material')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Shape" value={form.shape} onChange={updateField('shape')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Rim Type" value={form.rimType} onChange={updateField('rimType')} />
          </Grid>

          <Grid item xs={12}>
            <FormControl>
              <FormLabel id="manual-frame-lab-status">Frame To Lab</FormLabel>
              <RadioGroup
                row
                aria-labelledby="manual-frame-lab-status"
                value={form.frameLabStatus}
                onChange={updateField('frameLabStatus')}
              >
                <FormControlLabel value={FRAME_LAB_STATUS_ENCLOSED} control={<Radio size="small" />} label="Enclosed" />
                <FormControlLabel value={FRAME_LAB_STATUS_TO_COME} control={<Radio size="small" />} label="To Come" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">Estimated Total: {total.toFixed(2)}</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="contained" onClick={handleSave}>{primaryButtonLabel}</Button>
        <Button color="cancel" variant="outlined" onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ManualGlassesDialog;
